<template>
  <Teleport to="body">
    <transition name="age-gate-fade">
      <div 
        v-if="isOpen" 
        class="age-gate-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
      >
        <div class="age-gate-backdrop" @click="handleUnderageShake"></div>

        <div 
          class="age-gate-card"
          :class="{ 'shake-animation': isShaking }"
        >
          <!-- Brilho decorativo de fundo -->
          <div class="card-glow" aria-hidden="true"></div>

          <!-- Ícone / Badge de Confirmação -->
          <div class="age-badge-wrapper mb-3">
            <div class="age-badge-circle">
              <span class="age-badge-number">18+</span>
            </div>
          </div>

          <div class="age-gate-header mb-3">
            <span class="text-uppercase font-monospace age-gate-eyebrow">
              ✦ Controle de Acesso ✦
            </span>
            <h2 id="age-gate-title" class="age-gate-title font-cinzel mt-1">
               Você tem <strong>18 anos ou mais</strong>?
            </h2>
          </div>

          <div class="age-gate-body mb-4">
            <p class="age-gate-subtext">
              Não vale mentir, hein? O universo do Bar Experts é dedicado à arte da coquetelaria, técnicas de bar e bebidas alcoólicas.<br> Para explorar receitas autorais, dossiês e campeonatos, você precisa ter a idade legal permitida.
            </p>

            <div v-if="underageMessage" class="underage-warning p-3 rounded-3 mt-3">
              <i class="uil uil-info-circle me-1"></i>
              <span>{{ underageMessage }}</span>
            </div>
          </div>

          <div class="age-gate-actions d-flex flex-column flex-sm-row gap-2">
            <button 
              type="button" 
              class="btn btn-age-confirm flex-grow-1 py-3 px-4 rounded-pill fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2"
              @click="confirmAge"
            >
              <span>Tenho 18+</span>
            </button>

            <button 
              type="button" 
              class="btn btn-age-reject py-3 px-4 rounded-pill fw-semibold text-uppercase"
              @click="rejectAge"
            >
              <span>Não tenho</span>
            </button>
          </div>

          <div class="age-gate-footer mt-4">
            <small class="font-monospace d-flex align-items-center justify-content-center gap-2 age-footer-notice">
              <i class="uil uil-shield-check text-body-emphasis fs-6"></i>
              <span>Beba com responsabilidade.</span>
            </small>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const STORAGE_KEY = 'cbe_age_verified'
const isOpen = ref(false)
const isShaking = ref(false)
const underageMessage = ref('')
const countdown = ref(5)
const isRedirecting = ref(false)
let timer = null

onMounted(() => {
  try {
    const verified = localStorage.getItem(STORAGE_KEY)
    if (verified !== 'true') {
      isOpen.value = true
    }
  } catch (e) {
    isOpen.value = true
  }
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

function confirmAge() {
  if (timer) clearInterval(timer)
  try {
    localStorage.setItem(STORAGE_KEY, 'true')
  } catch (e) {}
  isOpen.value = false
}

function rejectAge() {
  handleUnderageShake()
  if (isRedirecting.value) return
  isRedirecting.value = true
  countdown.value = 5

  const updateMessage = () => {
    underageMessage.value = `A coquetelaria fica ainda melhor na hora certa! Conteúdo exclusivo para maiores de 18 anos. Redirecionando em ${countdown.value} segundo${countdown.value > 1 ? 's' : ''}...`
  }

  updateMessage()

  timer = setInterval(() => {
    countdown.value--
    if (countdown.value > 0) {
      updateMessage()
    } else {
      clearInterval(timer)
      window.location.href = 'https://www.youtube.com/results?search_query=infantil+educativo'
    }
  }, 1000)
}

function handleUnderageShake() {
  isShaking.value = true
  setTimeout(() => {
    isShaking.value = false
  }, 600)
}
</script>

<style scoped>
.age-gate-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.age-gate-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(5, 5, 8, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.age-gate-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 520px;
  background: radial-gradient(circle at 50% 0%, #1f1b24 0%, #100f13 100%);
  border: 1px solid rgba(220, 53, 69, 0.35);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.85),
              0 0 35px rgba(220, 53, 69, 0.25);
  border-radius: 1.5rem;
  padding: 2.25rem 2rem;
  text-align: center;
  color: #f8f9fa;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 250px;
  height: 120px;
  background: radial-gradient(circle, rgba(220, 53, 69, 0.35) 0%, transparent 70%);
  pointer-events: none;
}

.age-badge-wrapper {
  display: flex;
  justify-content: center;
}

.age-badge-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc3545 0%, #721c24 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(220, 53, 69, 0.45);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.age-badge-number {
  font-family: 'Cinzel', serif;
  font-size: 1.5rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.5px;
}

.age-gate-eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  letter-spacing: 1.5px;
  color: #e6a57e;
}

.age-gate-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
  letter-spacing: 0.5px;
}

.age-gate-question {
  font-size: 1.2rem;
  color: #f1f1f1;
  margin-bottom: 0.5rem;
}

.age-gate-question strong {
  color: #ea868f; /* modal sempre escuro: vermelho clarificado */
}

.age-gate-subtext {
  font-size: 0.92rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 auto;
  max-width: 440px;
}

.underage-warning {
  background: rgba(220, 53, 69, 0.15);
  border: 1px solid rgba(220, 53, 69, 0.4);
  color: #ff8591;
  font-size: 0.88rem;
  line-height: 1.5;
  animation: fadeIn 0.3s ease;
}

.btn-age-confirm {
  background: linear-gradient(135deg, #d32f2f 0%, #991b1b 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  letter-spacing: 0.5px;
  transition: all 0.25s ease;
  box-shadow: 0 8px 18px rgba(211, 47, 47, 0.35);
}

.btn-age-confirm:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%);
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(211, 47, 47, 0.5);
  color: #fff;
}

.btn-age-reject {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.btn-age-reject:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.age-gate-footer {
  font-size: 0.85rem;
}

.age-footer-notice {
  color: rgba(255, 255, 255, 0.92) !important;
  font-size: 0.84rem;
  font-weight: 500;
  letter-spacing: 0.4px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}

/* Animação de tremor caso tente fechar sendo menor */
.shake-animation {
  animation: cardShake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes cardShake {
  10%, 90% { transform: translate3d(-2px, 0, 0); }
  20%, 80% { transform: translate3d(4px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-6px, 0, 0); }
  40%, 60% { transform: translate3d(6px, 0, 0); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.age-gate-fade-enter-active,
.age-gate-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.age-gate-fade-enter-from,
.age-gate-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
</style>
